// StoryMessage.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes, css } from 'styled-components';
import { withScope, captureException, addBreadcrumb } from '@sentry/browser';
import { withTranslation } from 'react-i18next';

import UniversalComponent from '../component/UniversalComponent.jsx';
import { player as playerDebug } from '../resource/debug.js';
import { ButtonId, EventTypes } from '../resource/mixpanel.js';

import {
  getIsOnMobile,
  getIsOnIOS,
  getIsChrome,
} from '../resource/getUserAgent.js';
import withRouter from '../component/WithRouter.jsx';
import { DrmFeatureName } from '../resource/drmConstants.js';
import { drmRulesetTestStatus as drmRulesetTestStatusConstant } from '../resource/drmConstants.js';
import withFetchTranslation from '../resource/withFetchTranslation.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import Redirect from '../container/Redirect.js';

import {
  breakpoint,
  color,
  footerNavigationHeight,
} from '../style/variables.js';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import { Severity } from '../resource/sentry.js';
import getImageSupport from '../resource/getImageSupport.js';
// import getPublicSrcSet from '../resource/getPublicSrcSet.js';
import Tooltip from '../component/Tooltip.jsx';
import withPlayerTrackingEvent, {
  VIDEO_CATEGORY,
} from '../resource/withPlayerTrackingEvent.js';
import { PlayerType } from '../resource/engagementConstants.js';

import PlayIcon from '../../img/ic_play.svg';
import ArrowIcon from '../../img/ic-arrow-right-white.svg';
import CloseIcon from '../../img/ic-topbar-close.svg';
import BigPlayIcon from '../../img/ic-play-20.svg';
import BigPauseIcon from '../../img/ic-pause-20.svg';

const IMAGE_AUTO_CHANGE_TIME_SECS = 5; // TODO: remote config
const isServer = typeof window === 'undefined';

const playerDebugLog = playerDebug.extend('log:StoryMessage');
const playerError = playerDebug.extend('error:StoryMessage');
const playerLog = ({ messages, data }) => {
  addBreadcrumb({
    category: 'player',
    level: Severity.Info,
    message: messages.join(' '),
    data,
  });
  playerDebugLog(...messages, data);
};

const playerStateEnum = {
  waiting: 'waiting',
  loading: 'loading',
  playing: 'playing',
  paused: 'paused',
};
export class StoryMessage extends React.PureComponent {
  constructor(props) {
    super(props);

    const {
      messageId,
      senderId,
      senderName,
      fetchMessageDetail,
      fetchUser,
      staticContext,
      fetchMessageDetailError,
    } = props;
    // server side fetching
    if (staticContext.actions) {
      if (fetchMessageDetailError) {
        staticContext.error = fetchMessageDetailError;
      }
      if (messageId && !senderId) {
        staticContext.actions.push({
          function: fetchMessageDetail,
          argumentObject: {
            id: messageId,
            httpProxyHeaders: staticContext.httpProxyHeaders,
          },
        });
      }
      if (senderId && !senderName) {
        staticContext.actions.push({
          function: fetchUser,
          argumentObject: {
            id: senderId,
            httpProxyHeaders: staticContext.httpProxyHeaders,
          },
        });
      }
    }
  }

  option = {};
  posterFormat = getImageSupport().bestFormat;
  state = {
    currentTime: 0,
    duration: 100,
    playerState: 'waiting',
    initWaiting: true,
    canShowPlayButton: true,
    havePausedBefore: false,
    isPlaying: false,
  };

  getViewScope = async () => {
    const { messageId, isUnlocked, fetchViewScope } = this.props;
    if (!isUnlocked) {
      await fetchViewScope({ messageId });
    }
  };

  getPlayerThumbnail = () => {
    // const { ogMessageThumbnail: href } = this.props;
    // return getPublicSrcSet({ href, size: 512, format: this.posterFormat })?.src;

    // 2023.02.22
    // Temporarily cut out the poster to avoid the video out of shape on Safari,
    // especially for Stories and Feeds.
    return '';
  };

  componentDidMount() {
    const {
      senderId,
      fetchUserIsBlocked,
      location,
      setOperationChatroomData,
      fetchChatroomMediaMessages,
      listType,
      itemIds,
      usingList,
      fetchMessagesByUsingList,
      deviceWidth,
    } = this.props;
    const isOnDesktop = !getIsOnMobile() && deviceWidth > breakpoint.mobile;
    if (!isServer && isOnDesktop) {
      document.addEventListener('keydown', this.handleKeyDown);
    }

    this.getViewScope();

    fetchUserIsBlocked({ userId: senderId });
    setOperationChatroomData({
      key: 'clickChatroomMediaMessage',
      payload: {
        chatroomId: location?.state?.chatroomId,
        messageId: location?.state?.messageId,
        pageNumber: location?.state?.chatroomPageNumber,
      },
    });

    if (listType === 'chatroom') {
      fetchChatroomMediaMessages({ id: location?.state?.chatroomId });
    }
    if (!itemIds.length) {
      fetchMessagesByUsingList({ usingList });
    }
  }

  componentDidUpdate(prevProps) {
    const {
      messageId,
      senderId,
      isInputFocused,
      isMessageDetailGeneralModalOpen,
      isMessagePackUpsellingModalOpen,
      isAuthed,
      ogMessageThumbnail,
      isClikedShareButton,
      deviceWidth,
      setClickedShareButton,
      isBroadcastListExpanded,
      isUnlocked,
      trackPlayerEvent,
      timestampOffsetMilliseconds,
    } = this.props;
    const { isPlaying, havePausedBefore } = this.state;
    const isOnMobile = getIsOnMobile() && deviceWidth <= breakpoint.mobile;

    if (messageId !== prevProps.messageId) {
      this.clearTimerWhenChangeStory();
      this.setState({ currentTime: 0, duration: 100 });
      this.setState({ initWaiting: true });
      this.setState({ havePausedBefore: false });
      this.player?.poster(this.getPlayerThumbnail());
    }
    if (isInputFocused !== prevProps.isInputFocused) {
      if (isOnMobile || isPlaying) {
        this.letMediaPlayOrPause({ shouldPause: isInputFocused });
      }
    }
    if (
      isMessagePackUpsellingModalOpen !==
      prevProps.isMessagePackUpsellingModalOpen
    ) {
      if (isPlaying) {
        this.letMediaPlayOrPause({
          shouldPause: isMessagePackUpsellingModalOpen,
        });
      }
    }
    if (
      isMessageDetailGeneralModalOpen !==
      prevProps.isMessageDetailGeneralModalOpen
    ) {
      if (isOnMobile || isPlaying) {
        this.letMediaPlayOrPause({
          shouldPause: isMessageDetailGeneralModalOpen,
        });
      }
    }
    if (isBroadcastListExpanded !== prevProps.isBroadcastListExpanded) {
      if (isOnMobile || isPlaying) {
        this.letMediaPlayOrPause({
          shouldPause: isBroadcastListExpanded,
        });
      }
    }
    if (
      !isOnMobile &&
      isClikedShareButton &&
      isClikedShareButton !== prevProps.isClikedShareButton
    ) {
      !havePausedBefore && this.setState({ havePausedBefore: true });
      this.setState({ isPlaying: false });
      this.letMediaPlayOrPause({
        shouldPause: true,
      });
      setClickedShareButton({ messageId, clicked: false });
    }
    if (isAuthed !== prevProps.isAuthed || senderId !== prevProps.senderId) {
      this.props.fetchUserIsBlocked({ userId: this.props.senderId });
    }

    if (messageId !== prevProps.messageId) {
      this.getViewScope();
    }
    if (
      messageId !== prevProps.messageId ||
      isUnlocked !== prevProps.isUnlocked
    ) {
      trackPlayerEvent({
        timestamp: Date.now() + timestampOffsetMilliseconds,
        type: EventTypes.PLAYER_STOPPED,
        prevProps,
      });
      this.handleEngagementData({ isPlaying: false });
    }
    if (ogMessageThumbnail !== prevProps.ogMessageThumbnail) {
      this.player?.poster(this.getPlayerThumbnail());
    }
  }

  componentWillUnmount() {
    this.handleEngagementData({ isPlaying: false, isPlayerVisible: false });

    if (this.player) {
      // Just unassign `this.player` but not dispose the original instance.
      // We're gonna reuse it.
      this.player = null;
    }
    const { deviceWidth } = this.props;
    const isOnDesktop = !getIsOnMobile() && deviceWidth > breakpoint.mobile;
    if (!isServer && isOnDesktop) {
      document.removeEventListener('keydown', this.handleKeyDown);
    }
    this.clearTimerWhenChangeStory();
  }

  changeStory = direction => {
    const {
      replace,
      usingList,
      itemIds,
      storyPreviewIds,
      currentIndex,
      goBack,
      isNextPageFetching,
      isOnPostDetail,
      mediaType,
      listType,
      location,
    } = this.props;
    const messageId =
      direction === 1 ? itemIds[currentIndex + 1] : itemIds[currentIndex - 1];
    const isVideo = /^video/.test(mediaType);
    if (currentIndex + 2 === itemIds.length) {
      this.fetchMore();
    }
    if (messageId) {
      // 如果原本有 query string 然後不加上去，state 可能會不見❗
      let path;
      const isStoryPreview = storyPreviewIds.includes(messageId);
      if (isOnPostDetail) {
        path = isServer
          ? `/post/${messageId}`
          : `/post/${messageId}${location.search}`;
      } else {
        const isAtMessage = location.pathname.startsWith('/message/');
        path = `/${
          isStoryPreview ? 'story-preview' : isAtMessage ? 'message' : 'story'
        }/${messageId}${isServer ? '' : location.search}`;
      }
      replace(path, { listPath: usingList });
    } else if (direction === -1) {
      if (isVideo) {
        this.player?.currentTime(0);
      } else {
        this.setState({
          currentTime: 0,
        });
      }
    } else if (direction === 1 && !isNextPageFetching) {
      if (itemIds.length) {
        goBack();
      } else if (['chatroom', 'chat'].includes(listType) && isVideo) {
        const shouldPause = playerStateEnum.paused !== this.state.playerState;
        this.letMediaPlayOrPause({ shouldPause });
      } else return; // stay current page if not in list
    }
  };

  goToPreviousStory = () => {
    this.changeStory(-1);
  };

  goToNextStory = () => {
    this.changeStory(1);
  };

  fetchMore = () => {
    const { nextPage, usingList, fetchMessagesByUsingList } = this.props;
    if (nextPage) {
      fetchMessagesByUsingList({ usingList, page: nextPage });
    }
  };

  renderStoryPanel = messageId => {
    const { currentTime, duration, playerState, canShowPlayButton } =
      this.state;
    const { totalCount, mediaType, currentIndex, listType } = this.props;
    const isWaiting = playerState === playerStateEnum.waiting;
    const isLoading = playerState === playerStateEnum.loading;
    const isPaused = playerState === playerStateEnum.paused;
    const isImage = /image/.test(mediaType);

    return (
      <PanelWrapper>
        <UniversalComponent
          filename="Container_MessagePanel"
          currentIndex={currentIndex}
          showMultiProgress
          showThumbnail={isWaiting}
          showLoader={isWaiting || isLoading}
          showNotifier={!['chat', 'chatroom'].includes(listType)}
          totalCount={totalCount}
          importer={
            /* #__LOADABLE__ */ () => import('../container/MessagePanel.js')
          }
          id={messageId}
          currentTime={currentTime}
          duration={duration}
        />
        {isPaused && canShowPlayButton && !isImage && (
          <PlayButton onClick={this.handlePlayClick}>
            <img src={PlayIcon} alt="play" />
          </PlayButton>
        )}
      </PanelWrapper>
    );
  };

  handleEngagementData = ({ isPlaying, isPlayerVisible }) => {
    const {
      updateEngagementData,
      isFree,
      isNewlyUnlocked,
      viewScope,
      mediaType,
      messageId,
      senderId,
      senderName,
    } = this.props;

    if (this.player?.getAssetUri()) {
      updateEngagementData({
        playerType: PlayerType.VOD,
        isFree,
        isNewlyUnlocked,
        viewScope,
        mediaType,
        messageId,
        playerSource: this.player?.getAssetUri(),
        isPlaying,
        isPlayerVisible,
        creatorId: senderId,
        creatorUsername: senderName,
      });
    }
  };

  handleWaiting = () => {
    const { initWaiting } = this.state;
    if (initWaiting) {
      this.setState({ initWaiting: false });
      this.setState({ playerState: playerStateEnum.waiting });
    } else {
      this.setState({ playerState: playerStateEnum.loading });
    }
  };
  handlePlay = event => {
    const { timestampOffsetMilliseconds } = this.props;
    const { playerState } = this.state;
    if (event.type === playerStateEnum.playing) {
      this.props.trackPlayerEvent({
        timestamp: Date.now() + timestampOffsetMilliseconds,
        type: EventTypes.PLAYER_STARTED,
      });

      this.handleEngagementData({ isPlaying: true, isPlayerVisible: true });
    }
    if (playerState !== playerStateEnum.playing) {
      // TODO: sometimes doesn't work, ex: chatroom message
      this.setState({ playerState: playerStateEnum.playing });
    }
  };
  handlePause = () => {
    const { mediaType, timestampOffsetMilliseconds } = this.props;
    if (/video/.test(mediaType)) {
      this.props.trackPlayerEvent({
        timestamp: Date.now() + timestampOffsetMilliseconds,
        type: EventTypes.PLAYER_STOPPED,
      });
      this.handleEngagementData({ isPlaying: false });
    } // Images are recorded when the timer is over
    this.setState({ playerState: playerStateEnum.paused });
  };
  handleCanPlay = () => {
    const { mediaType } = this.props;
    this.setState({ isPlaying: true });
    const isImage = /image/.test(mediaType);
    if (isImage) {
      this.setState({
        duration: IMAGE_AUTO_CHANGE_TIME_SECS,
      });
      this.playImage();
    } else {
      // autoplaying image on some Android may meet error,
      // will still try to play with both video and unknown mediaType.
      this.playVideo('canplay event');
    }
    this.checkMediaType();
  };
  handleProgressUpdate = ({ currentTime, duration }) => {
    if (isNaN(duration)) return;
    this.setState({ currentTime, duration });
  };

  handlePlayClick = () => {
    this.playVideo('play button clicked');
  };
  handlePlayerSetup = player => {
    const { timestampOffsetMilliseconds } = this.props;
    this.player = player;
    this.props.trackPlayerEvent({
      type: EventTypes.PLAYER_VISIBLE,
      timestamp: Date.now() + timestampOffsetMilliseconds,
    });
    this.player.poster(this.getPlayerThumbnail());

    this.handleEngagementData({ isPlayerVisible: true });
  };
  handleLoadedMetadata = () => {
    const { assetIds } = this.props;
    if (getIsOnIOS() || !getIsChrome() || assetIds[0]) {
      try {
        // iOS and Edge won't load video when autoplay turned off and asset source changed
        // which also won't display first frame and trigger change story timer,
        // so we need to manually trigger it.
        this.playVideo('loadedmetadata');
      } catch (error) {
        playerError('handleLoadedMetadata', error);
      }
    }
  };

  checkMediaType = () => {
    const { mediaType } = this.props;
    if (mediaType) return;

    window.clearTimeout(this.checkMediaTypeTimer);
    return (this.checkMediaTypeTimer = setTimeout(this.checkMediaType, 500)); // TODO: remote config
  };

  clearTimerWhenChangeStory = () => {
    window.clearTimeout(this.checkMediaTypeTimer);
    window.clearTimeout(this.handleEndedTimer);
    window.clearTimeout(this.playingImageId);
  };

  clearPlayingImageTimer = () => {
    clearInterval(this.playingImageId);
    this.playingImageId = null;
  };

  handleEnded = () => {
    const { mediaType, shouldLoop } = this.props;

    if (!mediaType) {
      return (this.handleEndedTimer = setTimeout(() => {
        this.handleEnded();
      }, 500)); // TODO: remote config
    }

    if (mediaType && /video/.test(mediaType) && !shouldLoop) {
      this.goToNextStory();
    }
  };

  playImage = () => {
    if (this.playingImageId) {
      this.clearPlayingImageTimer();
    }

    this.playingImageId = window.setInterval(() => {
      const { currentTime } = this.state;
      const { timestampOffsetMilliseconds } = this.props;
      this.setState({
        currentTime: currentTime + 0.1,
      });

      if (currentTime >= IMAGE_AUTO_CHANGE_TIME_SECS) {
        this.props.trackPlayerEvent({
          timestamp: Date.now() + timestampOffsetMilliseconds,
          type: EventTypes.PLAYER_STOPPED,
        });
        this.handleEngagementData({ isPlaying: false });
        window.clearInterval(this.playingImageId);
        this.goToNextStory();
      }
    }, 100); // TODO: remote config
  };

  playVideo = actionName => {
    const { mediaType } = this.props;
    playerLog({
      messages: ['playVideo', 'init'],
      data: { actionName, mediaType },
    });
    this.player
      .play()
      .then(() => {
        playerLog({
          messages: ['playVideo', 'play() promise done'],
          data: { actionName, mediaType },
        });
      })
      .catch(error => {
        playerError('playVideo', actionName, error);
        this.setState({ playerState: playerStateEnum.paused });
        if (error.name === 'NotAllowedError') {
          return;
        }
        withScope(scope => {
          scope.setFingerprint(['message-detail', error.name]);
          scope.setExtra('action', actionName);
          scope.setExtra('playState', this.state.playerState);
          captureException(error);
        });
      });
  };

  handleTouchStart = e => {
    e.preventDefault();
    this.touchId = setTimeout(() => {
      this.letMediaPlayOrPause({ shouldPause: true });
    }, 200); // TODO: remote config
  };

  handleTouchEnd = type => e => {
    const { playerState } = this.state;
    const { mediaType } = this.props;
    e.preventDefault();
    this.touchId && clearTimeout(this.touchId);

    const isImage = /image/.test(mediaType);
    const isPaused = isImage
      ? !this.playingImageId && this.playingImageId !== undefined
      : playerStateEnum.paused === playerState;

    if (isPaused) {
      this.letMediaPlayOrPause({ shouldPause: false });
    } else {
      type === 'next' ? this.goToNextStory() : this.goToPreviousStory();
    }
  };

  handleKeyDown = e => {
    const { goBack } = this.props;
    if (e.key === 'ArrowLeft') {
      this.goToPreviousStory();
    } else if (e.key === 'ArrowRight') {
      this.goToNextStory();
    } else if (e.key === 'Escape') {
      goBack();
    }
  };

  letMediaPlayOrPause = ({ shouldPause }) => {
    const { mediaType } = this.props;
    if (/video/.test(mediaType)) {
      if (shouldPause) {
        const pauseCallback = () => this.player?.pause();
        this.setState({ canShowPlayButton: !shouldPause }, pauseCallback);
      } else {
        this.player?.play();
        this.setState({ canShowPlayButton: shouldPause });
      }
    } else {
      if (shouldPause) {
        this.clearPlayingImageTimer();
      } else {
        this.playImage();
      }
    }
  };

  handleOnClickDesktopActionPanel = () => {
    const { havePausedBefore, playerState } = this.state;
    const { mediaType } = this.props;

    if (!havePausedBefore) {
      this.setState({
        havePausedBefore: true,
      });
    }

    const isImage = /image/.test(mediaType);
    const isPlaying = isImage
      ? !!this.playingImageId
      : playerStateEnum.playing === playerState;

    this.setState({
      isPlaying: !isPlaying,
    });
    this.letMediaPlayOrPause({
      shouldPause: isPlaying,
    });
  };

  handleCloseButton = () => {
    const { goBack } = this.props;
    goBack();
  };

  renderPlayer = id => {
    const {
      type,
      drmTestStatus,
      isPlayerMuted,
      shouldLoop,
      isUnlocked,
      isVolumeButtonEnabled,
      caption,
      mediaType,
      assetIds,
    } = this.props;
    if (drmRulesetTestStatusConstant.TESTING === drmTestStatus) {
      return null;
    }
    const isImage = /image/.test(mediaType);
    // autoplaying image on some Android may meet error,
    // will still try to play with both video and unknown mediaType.
    const autoplay = isImage || !mediaType ? false : 'muted';
    // mute all image or unknown mediaType to prevent meet mobile browser autoplay policy.
    const shouldMute =
      isImage ||
      !mediaType ||
      (isVolumeButtonEnabled || isUnlocked ? isPlayerMuted : true);
    const nextOption = {
      loop: shouldLoop && !isImage,
      autoplay,
      poster: this.getPlayerThumbnail(),
    };
    if (Object.keys(nextOption).length === Object.keys(this.option).length) {
      const isOptionChanged = Object.entries(nextOption).some(
        ([key, value]) => value !== this.option[key]
      );
      if (isOptionChanged) this.option = nextOption;
    } else {
      this.option = nextOption;
    }
    return (
      <PlayerMainWrapper>
        <UniversalComponent
          filename="Container_MessageVideoPlayer"
          drmFeatureName={
            ['chat', 'reply'].includes(type)
              ? DrmFeatureName.CHAT
              : DrmFeatureName.STORY
          }
          option={this.option}
          importer={
            /* #__LOADABLE__ */ () =>
              import('../container/MessageVideoPlayer.js')
          }
          messageId={id}
          assetId={assetIds[0]}
          alt={caption}
          isMuted={shouldMute}
          onWaiting={this.handleWaiting}
          onPlaying={this.handlePlay}
          onCanplaythrough={this.handlePlay}
          onPause={this.handlePause}
          onSetup={this.handlePlayerSetup}
          onEnded={this.handleEnded}
          onProgressUpdate={this.handleProgressUpdate}
          onLoadedMetadata={this.handleLoadedMetadata}
          onCanPlay={this.handleCanPlay}
        />
      </PlayerMainWrapper>
    );
  };

  renderActionPanel = () => {
    const { isInputFocused, deviceWidth } = this.props;
    const { playerState, isPlaying } = this.state;
    const isOnDesktop = !getIsOnMobile() && deviceWidth > breakpoint.mobile;

    if (
      isOnDesktop &&
      (playerState === playerStateEnum.waiting ||
        playerState === playerStateEnum.loading)
    ) {
      return null;
    }

    return (
      <ActionPanelWrapper>
        {isOnDesktop ? (
          <DesktopActionPanel
            role="button"
            tabIndex={0}
            aria-label={isPlaying ? 'pause' : 'play'}
            onClick={this.handleOnClickDesktopActionPanel}
          />
        ) : (
          <ActionPanel>
            <ActionPanelLeft
              onTouchStart={this.handleTouchStart}
              onTouchEnd={this.handleTouchEnd('previous')}
            />
            <ActionPanelRight
              onTouchStart={this.handleTouchStart}
              onTouchEnd={this.handleTouchEnd('next')}
            />
          </ActionPanel>
        )}
        {isInputFocused && <PanelGuard />}
      </ActionPanelWrapper>
    );
  };

  renderNavigation = () => {
    const { t, itemIds, currentIndex } = this.props;
    const hasPrevMessage = itemIds[currentIndex - 1];
    const hasNextMessage = itemIds[currentIndex + 1];
    return (
      <>
        {hasPrevMessage && (
          <LeftSidebar onClick={this.goToPreviousStory}>
            <LeftNavigationIcon>
              <Tooltip
                position="top"
                renderHint={() => (
                  <LeftNavigationTooltip>
                    {t('previous_content', {
                      ns: TranslationNamespace.POST,
                    })}
                  </LeftNavigationTooltip>
                )}
                trigger="hover"
              >
                <img src={ArrowIcon} width={40} height={40} alt="left arrow" />
              </Tooltip>
            </LeftNavigationIcon>
          </LeftSidebar>
        )}

        {hasNextMessage && (
          <RightSidebar onClick={this.goToNextStory}>
            <RightNavigationIcon>
              <Tooltip
                position="top"
                renderHint={() => (
                  <RightNavigationTooltip>
                    {t('next_content', {
                      ns: TranslationNamespace.POST,
                    })}
                  </RightNavigationTooltip>
                )}
                trigger="hover"
              >
                <img src={ArrowIcon} width={40} height={40} alt="right arrow" />
              </Tooltip>
            </RightNavigationIcon>
          </RightSidebar>
        )}
      </>
    );
  };

  renderPlayPauseButton = () => {
    const { havePausedBefore, isPlaying } = this.state;
    if (!havePausedBefore) {
      return null;
    }

    return (
      <PlayPauseButtonWrapper>
        <PlayPauseButton isPlayButton={isPlaying}>
          {isPlaying ? (
            <img src={BigPlayIcon} alt="play" />
          ) : (
            <img src={BigPauseIcon} alt="pause" />
          )}
        </PlayPauseButton>
      </PlayPauseButtonWrapper>
    );
  };

  renderCloseButton = () => {
    const { t } = this.props;
    return (
      <CloseButtonWrapper>
        <Tooltip
          position="top"
          renderHint={() => (
            <CloseButtonTooltip>
              {t('general_close', {
                ns: TranslationNamespace.GENERAL,
              })}
            </CloseButtonTooltip>
          )}
          trigger="hover"
        >
          <CloseButton
            data-element_id={ButtonId.All.ButtonClose}
            onClick={this.handleCloseButton}
          >
            <img src={CloseIcon} width={24} height={24} alt="close" />
          </CloseButton>
        </Tooltip>
      </CloseButtonWrapper>
    );
  };

  render() {
    const { messageId, fetchMessageDetailError } = this.props;
    if (fetchMessageDetailError?.message) {
      let status;
      try {
        status = JSON.parse(fetchMessageDetailError.message)?.status;
      } catch (_) {
        //
      }
      return (
        <Redirect
          to={status === 404 || status === 403 ? '/404' : '/error'}
          status={status}
          replace={true}
        />
      );
    }

    return (
      <>
        <StyledStoryMessage>
          {this.renderPlayer(messageId)}
          {this.renderPlayPauseButton()}
          {this.renderActionPanel()}
          {this.renderStoryPanel(messageId)}
          {this.renderNavigation()}
        </StyledStoryMessage>
        {this.renderCloseButton()}
      </>
    );
  }
}

StoryMessage.propTypes = {
  t: PropTypes.func.isRequired,
  drmTestStatus: PropTypes.string,
  isInputFocused: PropTypes.bool,
  messageId: PropTypes.string,
  isPlayerMuted: PropTypes.bool,
  shouldLoop: PropTypes.bool,
  caption: PropTypes.string,
  totalCount: PropTypes.number,
  staticContext: PropTypes.object,
  currentIndex: PropTypes.number,
  deviceWidth: PropTypes.number,
  timestampOffsetMilliseconds: PropTypes.number,
  senderName: PropTypes.string,
  ogMessageThumbnail: PropTypes.string,
  isBroadcastListExpanded: PropTypes.bool,
  isNextPageFetching: PropTypes.bool,
  storyPreviewIds: PropTypes.array,
  itemIds: PropTypes.array,
  assetIds: PropTypes.array,
  usingList: PropTypes.array,
  listType: PropTypes.string,
  isUnlocked: PropTypes.bool,
  senderId: PropTypes.string,
  location: PropTypes.object,
  fetchViewScope: PropTypes.func,
  nextPage: PropTypes.number,
  mediaType: PropTypes.string,
  type: PropTypes.string,
  isMessageDetailGeneralModalOpen: PropTypes.bool,
  isMessagePackUpsellingModalOpen: PropTypes.bool,
  isAuthed: PropTypes.bool,
  isOnPostDetail: PropTypes.bool,
  isVolumeButtonEnabled: PropTypes.bool,
  fetchUserIsBlocked: PropTypes.func,
  isClikedShareButton: PropTypes.bool,
  viewScope: PropTypes.string,
  isFree: PropTypes.bool,
  isNewlyUnlocked: PropTypes.bool,
  replace: PropTypes.func,
  goBack: PropTypes.func,
  fetchMessageDetail: PropTypes.func,
  fetchMessageDetailError: PropTypes.instanceOf(Error),
  fetchUser: PropTypes.func,
  fetchMessagesByUsingList: PropTypes.func,
  setOperationChatroomData: PropTypes.func,
  fetchChatroomMediaMessages: PropTypes.func,
  setClickedShareButton: PropTypes.func,
  trackPlayerEvent: PropTypes.func,
  updateEngagementData: PropTypes.func,
};

StoryMessage.defaultProps = {
  drmTestStatus: '',
  isInputFocused: false,
  messageId: '',
  staticContext: {},
  isPlayerMuted: false,
  shouldLoop: false,
  location: {},
  fetchMessageDetailError: null,
  caption: '',
  senderId: '',
  senderName: '',
  ogMessageThumbnail: '',
  mediaType: '',
  isBroadcastListExpanded: '',
  type: null,
  isUnlocked: false,
  totalCount: 0,
  currentIndex: 0,
  deviceWidth: 0,
  storyPreviewIds: [],
  itemIds: [],
  assetIds: [],
  usingList: [],
  listType: '',
  nextPage: 0,
  timestampOffsetMilliseconds: 0,
  isMessageDetailGeneralModalOpen: false,
  isMessagePackUpsellingModalOpen: false,
  isAuthed: false,
  isOnPostDetail: false,
  isVolumeButtonEnabled: false,
  isNextPageFetching: false,
  isClikedShareButton: false,
  isFree: null,
  isNewlyUnlocked: null,
  fetchUserIsBlocked: () => null,
  fetchViewScope: () => null,
  replace: () => null,
  goBack: () => null,
  fetchUser: () => null,
  fetchMessageDetail: () => null,
  fetchMessagesByUsingList: () => null,
  setOperationChatroomData: () => null,
  fetchChatroomMediaMessages: () => null,
  setClickedShareButton: () => null,
  trackPlayerEvent: () => null,
  updateEngagementData: () => null,
};

const PanelGuard = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const ActionPanelWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const ActionPanel = styled.div`
  display: flex;
  height: 100%;
`;

const ActionPanelLeft = styled.div`
  flex-grow: 1;
`;
const ActionPanelRight = styled.div`
  flex-grow: 2;
`;

const DesktopActionPanel = styled.div`
  width: 100%;
  height: 100%;
`;

const StyledStoryMessage = styled.div`
  position: fixed;
  top: var(--space-to-pinned-notification);
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  height: calc(
    100% - var(--space-to-pinned-notification) - ${footerNavigationHeight}px
  );

  @media (min-width: ${breakpoint.mobile}px) {
    width: auto;
    height: calc(100% - 104px - var(--space-to-pinned-notification));
    top: calc(84px + var(--space-to-pinned-notification));
    bottom: 20px;
    left: 50%;
    transform: translate(-50%, 0%);
  }

  @media (min-width: ${breakpoint.mobile}px) and (min-height: 900px) {
    aspect-ratio: 9 / 16;
  }
`;

const PlayButton = styled.button`
  ${ResetButtonStyle};
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  padding: 10px;
  width: 72px;
  height: 72px;
  pointer-events: auto;
  background-color: rgba(25, 25, 25, 0.6);
  & > img {
    width: 100%;
    height: 100%;
  }
`;

const PanelWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

const PlayerMainWrapper = styled.main`
  width: 100%;
  height: 100%;
`;

const Sidebar = styled.button`
  ${ResetButtonStyle}
  height: 100%;
  width: 76px;
  position: absolute;
  top: 50%;
  transform: translate(0%, -50%);
  display: flex;
  justify-content: center;
  align-items: center;
  > div {
    background-color: rgba(155, 155, 155, 0.2);
    opacity: 0.5;
  }
  @media (hover: hover) {
    :hover {
      > div {
        opacity: 1;
        background-color: rgba(155, 155, 155, 0.2);
      }
    }
  }
`;

const LeftSidebar = styled(Sidebar)`
  left: -76px;
  @media (hover: hover) {
    :hover {
      > div {
        transform: translateX(-5px);
      }
    }
  }
`;

const RightSidebar = styled(Sidebar)`
  right: -76px;
  @media (hover: hover) {
    :hover {
      > div {
        transform: translateX(5px);
      }
    }
  }
`;

const NavigationIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  transition: 0.3s;

  background-color: rgba(25, 25, 25, 0.2);
`;

const LeftNavigationIcon = styled(NavigationIcon)`
  div > img {
    transform: rotate(180deg);
  }
`;

const RightNavigationIcon = styled(NavigationIcon)``;

const TooltipStyle = styled.div`
  min-width: 58px;
  height: 30px;
  padding: 8px;
  border-radius: 8px;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.5);
  background-color: rgba(246, 246, 246, 0.8);
  color: ${color.black};
  text-align: center;
  white-space: nowrap;
  font-size: 14px;
`;

const RightNavigationTooltip = styled(TooltipStyle)`
  margin-left: 20px;
`;

const LeftNavigationTooltip = styled(TooltipStyle)`
  margin-left: -38px;
`;

const PlayPauseButtonWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: none;

  @media (min-width: ${breakpoint.mobile}px) {
    display: block;
  }
`;

const ScalinfFadeOut = keyframes`
  from  {
    opacity: 1;
    transform: scale(1);
  }
  to  {
    opacity: 0;
    transform: scale(2);
  }
`;

const PlayPauseButton = styled.div`
  width: 98px;
  height: 98px;
  background-color: rgba(25, 25, 25, 0.6);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;

  ${({ isPlayButton }) => {
    if (isPlayButton)
      return css`
        animation: 500ms forwards ${ScalinfFadeOut} ease-in;
      `;
  }}
`;

const CloseButtonWrapper = styled.div`
  display: none;
  @media (min-width: ${breakpoint.mobile}px) {
    width: 32px;
    height: 32px;
    position: fixed;
    top: calc(84px + var(--space-to-pinned-notification));
    right: 20px;
    display: block;
    z-index: 10;
  }
`;

const CloseButton = styled.button`
  ${ResetButtonStyle}
  width: 32px;
  height: 32px;
  border-radius: 50%;
  position: fixed;
  top: calc(84px + var(--space-to-pinned-notification));
  right: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(25, 25, 25, 0.2);
  @media (hover: hover) {
    :hover {
      background-color: rgba(155, 155, 155, 0.2);
    }
  }
`;

const CloseButtonTooltip = styled.div`
  min-width: 44px;
  height: 30px;
  padding: 8px;
  border-radius: 8px;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.5);
  background-color: rgba(246, 246, 246, 0.8);
  margin-left: -22px;
  font-size: 14px;
  text-align: center;
  white-space: nowrap;
  color: ${color.black};
`;

export default withTranslation()(
  withRouter(
    withFetchTranslation(
      withPlayerTrackingEvent(StoryMessage, VIDEO_CATEGORY.STORY)
    )
  )
);
